import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import authProvider from '../providers/authProvider';
import { QualityIcon } from '../resources/quality';
import Icon from './Icon';

/**
 * Componente de selección de aplicación/servicio al hacer login
 * Muestra las aplicaciones disponibles con el mismo diseño que AppsMenu
 */
const seconds = 20;
const AppSelector = () => {
  const navigate = useNavigate();
  const {
    userServices,
    isAdmin,
    switchApp,
    getAvailableApps,
    refreshUserData,
  } = useAppContext();
  console.log(isAdmin);

  const [isLoading, setIsLoading] = useState(true);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [countdown, setCountdown] = useState(seconds);

  useEffect(() => {
    // Cargar datos del usuario desde el contexto (que ya los tiene desde localStorage)
    const loadUserData = async () => {
      try {
        // Refrescar datos del usuario desde localStorage para asegurar que estén actualizados
        refreshUserData();
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading user data:', error);
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [refreshUserData]);

  // Temporizador para logout automático cuando no hay permisos
  useEffect(() => {
    if (userServices.length === 0 && !isAdmin && !isLoading) {
      setShowAccessDenied(true);
      setCountdown(seconds); // Reiniciar contador

      const timer = setTimeout(async () => {
        try {
          await authProvider.logout();
          // Redirigir a la raíz donde DashboardWrapper manejará la redirección al login
          navigate('/', { replace: true });
        } catch (error) {
          console.error('Error during automatic logout:', error);
          // En caso de error, forzar redirección
          navigate('/', { replace: true });
        }
      }, seconds * 1000); // miliseconds

      return () => clearTimeout(timer);
    } else {
      setShowAccessDenied(false);
    }
  }, [userServices, isAdmin, isLoading, navigate]);

  // Efecto para la cuenta atrás
  useEffect(() => {
    let interval;
    if (showAccessDenied && countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showAccessDenied, countdown]);

  const availableApps = getAvailableApps();

  // Si está cargando, mostrar spinner
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Si no hay servicios configurados y no es admin
  if (showAccessDenied) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Acceso denegado
              </Typography>
              <Typography variant="body1">
                No tiene permisos asignados para acceder a ninguna aplicación.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                Será redirigido al login en {countdown} segundo
                {countdown !== 1 ? 's' : ''}...
              </Typography>
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Por favor, contacte con un administrador para que le asigne los
              permisos necesarios para acceder a la aplicación.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Función para manejar la selección de aplicación
  const handleAppSelect = appType => {
    switchApp(appType);
    // Marcar que el usuario ha seleccionado una aplicación
    localStorage.setItem('appSelected', 'true');
    // Redirigir al dashboard correspondiente
    navigate(appType === 'quality' ? '/quality' : '/', {
      replace: true,
    });
  };

  const handleAdminAccess = () => {
    navigate('/user-management', { replace: true });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 3,
        backgroundColor: '#f5f5f5',
      }}
    >
      <Card sx={{ maxWidth: 800, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Seleccionar aplicación
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mb: 4 }}
          >
            Selecciona la aplicación a la que deseas acceder:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Grid de aplicaciones disponibles */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(auto-fit, minmax(200px, 1fr))',
                },
                gap: 3,
                justifyItems: 'center',
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              {/* Scuore Card App */}
              {availableApps.includes('card') && (
                <Paper
                  elevation={0}
                  onClick={() => handleAppSelect('card')}
                  sx={{
                    padding: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    border: '2px solid transparent',
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                      borderColor: '#8955F2',
                    },
                    minHeight: 140,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#8955F2',
                      borderRadius: '50%',
                      padding: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#7B4AE0',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <Icon
                      variant="card"
                      sx={{ color: 'white', fontSize: 32 }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#202124',
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: 1.3,
                    }}
                  >
                    Scuore Card
                  </Typography>
                </Paper>
              )}

              {/* Scuore Quality App */}
              {availableApps.includes('quality') && (
                <Paper
                  elevation={0}
                  onClick={() => handleAppSelect('quality')}
                  sx={{
                    padding: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    border: '2px solid transparent',
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                      borderColor: '#3F49DB',
                    },
                    minHeight: 140,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#3F49DB',
                      borderRadius: '50%',
                      padding: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#3441C3',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <QualityIcon sx={{ color: 'white', fontSize: 32 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#202124',
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: 1.3,
                    }}
                  >
                    Scuore Quality
                  </Typography>
                </Paper>
              )}
            </Box>

            {/* Botón de administración para admins */}
            {isAdmin && (
              <>
                <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 3, mt: 2 }}>
                  <Typography variant="h6" gutterBottom align="center">
                    Administración
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleAdminAccess}
                    sx={{
                      py: 2,
                      px: 4,
                      borderColor: '#666',
                      color: '#666',
                      '&:hover': {
                        borderColor: '#333',
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    <Typography variant="h6">Gestionar usuarios</Typography>
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AppSelector;
